"use client";

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Icon from "@/components/library/Icon";
import { VoiceGender } from "@/enums/agencies";
import { useOrg } from "@/hooks/useOrg";
import { VoiceOption } from "@/types/agencies";
import { orgLanguageChipsForCatalog } from "@/utils/scripts/groupVoicesByLanguage";
import VoiceFilterChip from "./VoiceFilterChip";
import { VoiceFilters } from "./VoiceSelectorFilters";

interface VoiceExploreToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  filters: VoiceFilters;
  onFiltersChange: (filters: VoiceFilters) => void;
  catalog: readonly VoiceOption[];
}

function toggleValue<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

const GENDER_CHIPS = [VoiceGender.FEMALE, VoiceGender.MALE] as const;

export default function VoiceExploreToolbar({
  search,
  onSearchChange,
  filters,
  onFiltersChange,
  catalog,
}: Readonly<VoiceExploreToolbarProps>) {
  const { t: ct } = useTranslation("common");
  const { t: st } = useTranslation("settings");
  const { agentProfile } = useOrg();
  const languageChips = useMemo(
    () => orgLanguageChipsForCatalog(agentProfile.languages, catalog),
    [agentProfile.languages, catalog]
  );

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="relative w-44 shrink-0">
        <Icon
          icon="magnifying-glass"
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
        />
        <input
          type="text"
          placeholder={ct("search")}
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          className="w-full pl-9 pr-3 py-1.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
      </div>

      {GENDER_CHIPS.map((gender) => (
        <VoiceFilterChip
          key={gender}
          label={st(gender)}
          selected={filters.genders.includes(gender)}
          onToggle={() =>
            onFiltersChange({
              ...filters,
              genders: toggleValue(filters.genders, gender),
            })
          }
        />
      ))}

      {languageChips.map((language) => (
        <VoiceFilterChip
          key={language}
          label={ct(language)}
          selected={filters.languages.includes(language)}
          onToggle={() =>
            onFiltersChange({
              ...filters,
              languages: toggleValue(filters.languages, language),
            })
          }
        />
      ))}
    </div>
  );
}
